import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { CheckCircle, XCircle, Mail, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await api.post('/verification/verify', { token });
      setStatus('success');
      setMessage(res.data.message);
      setEmail(res.data.email);
      toast.success('Email verified successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed');
      toast.error('Verification failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <Toaster position="top-center" richColors />
      
      <div className="max-w-md mx-auto px-4 py-20">
        <Card className="bg-[#111] border-[#333]">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              {status === 'loading' && (
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              )}
              {status === 'success' && (
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              )}
              {status === 'error' && (
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
              )}
            </div>
            <CardTitle className="text-white text-2xl">
              {status === 'loading' && 'Verifying Email...'}
              {status === 'success' && 'Email Verified!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-white/70">
              {status === 'loading' && 'Please wait while we verify your email address...'}
              {status === 'success' && (
                <>
                  Your email <span className="text-[#FF007F] font-medium">{email}</span> has been verified successfully.
                  You'll now receive all important updates about your classes.
                </>
              )}
              {status === 'error' && message}
            </p>
            
            {status === 'success' && (
              <Button 
                onClick={() => navigate('/parent-dashboard')}
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
              >
                Go to Dashboard
              </Button>
            )}
            
            {status === 'error' && (
              <div className="space-y-2">
                <Button 
                  onClick={() => navigate('/parent-dashboard')}
                  variant="outline"
                  className="border-[#333] text-white hover:bg-[#222]"
                >
                  Go to Dashboard
                </Button>
                <p className="text-xs text-white/50">
                  You can request a new verification link from your dashboard
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
